import type { ReactNode } from "react";

import type { ComponentPlacementLocation } from "@intelligems/ig-types/dtos/components";
import { IgNumericalCounter } from "@intelligems/ig-theme/IgNumericalCounter";
import { IgSwitch } from "@intelligems/ig-theme/IgSwitch";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import Stack from "@mui/material/Stack";

import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";

interface GeneralSectionProps {
  location: ComponentPlacementLocation;
  // A rule-bound container takes its exclusions from the Agentic Logic rule.
  showExclusions: boolean;
  maxProducts: number;
  excludeOutOfStock: boolean;
  excludeItemsInCart: boolean;
  excludePdpProduct: boolean;
  onChange: (changes: {
    maxProducts?: number;
    excludeOutOfStock?: boolean;
    excludeItemsInCart?: boolean;
    excludePdpProduct?: boolean;
  }) => void;
  children?: ReactNode;
}

// Product-selection settings, shown above the rules.
export const GeneralSection = ({
  location,
  showExclusions,
  maxProducts,
  excludeOutOfStock,
  excludeItemsInCart,
  excludePdpProduct,
  onChange,
  children,
}: GeneralSectionProps) => (
  <Stack spacing={5}>
    <Panel.Header>General</Panel.Header>
    {showExclusions && (
      <>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <IgTypography>Exclude out of stock items</IgTypography>
          <IgSwitch
            checked={excludeOutOfStock}
            onChange={(_, checked) => onChange({ excludeOutOfStock: checked })}
          />
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <IgTypography>Exclude products in cart</IgTypography>
          <IgSwitch
            checked={excludeItemsInCart}
            onChange={(_, checked) => onChange({ excludeItemsInCart: checked })}
          />
        </Stack>
        {location === "pdp" && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <IgTypography>Exclude the product being viewed</IgTypography>
            <IgSwitch
              checked={excludePdpProduct}
              onChange={(_, checked) =>
                onChange({ excludePdpProduct: checked })
              }
            />
          </Stack>
        )}
      </>
    )}
    {children}
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <IgTypography>Number of products to show</IgTypography>
      <IgNumericalCounter
        value={maxProducts}
        min={1}
        max={10}
        onChange={(value) => value != null && onChange({ maxProducts: value })}
      />
    </Stack>
  </Stack>
);
